import React, { useContext, useEffect, useState } from 'react';
import {
  CarrierQuoteStateEnum,
  CustomerQuoteStateEnum,
  DistributionTypeEnum,
  EntityTypeEnum,
  ExpeditionStatusEnum,
  MakeOfferLocationSlotInDTO,
  MakeOfferTransportLegInDTO,
  QuoteDetailOutDTO,
} from '@api/ringil-api.v2';
import { formatHandlingUnits, formatWeight, isNullOrUndefined } from '@utils/utils';
import { dayjsFromApiUtc, formatDateTimeFromDayjsInTzWithLocale } from '@utils/dateWithTz.tsx';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import ReconfirmQuote from '../common/ReconfirmQuote';
import { ApiContext } from '@api/api';
import dayjs, { Dayjs } from 'dayjs';
import useNavigation from '@hooks/useNavigation';
import { useTranslation } from 'react-i18next';
import DemandDataProvider, { DemandDetailContext } from '@app/pages/carrier/demand-detail/DemandDetailActions.tsx';
import { DemandDetailView } from '@app/pages/carrier/demand-detail/types.ts';
import DemandStateSpan from '@components/obsolete/StateSpan/DemandStateSpan.tsx';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import AlertInfo from '@components/obsolete/Alert/AlertInfo.tsx';
import NavTitle from '@components/obsolete/NavTitle/NavTitle.tsx';
import ListInfo from '@components/obsolete/ListInfo/ListInfo.tsx';
import ListInfoItem from '@components/obsolete/ListInfo/ListInfoItem.tsx';
import { useParams } from 'react-router-dom';
import MultipleValues from '@components/ringil3/HelperComponents/MultipleValues.tsx';
import CarrierQuote from '@app/pages/carrier/demand-detail/CarrierQuote.tsx';
import { OfferTypeEnum } from '@app/pages/carrier/demand-detail/carrierQuoteUtils.tsx';
import RouteDescriptionComponent from '@app/pages/carrier/demand-detail/RouteDescription/RouteDescriptionComponent.tsx';
import CarrierChatComponent from '@app/pages/carrier/Chat/CarrierChatComponent.tsx';
import { formatPrice } from '@utils/priceUtils.ts';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { useCreateAttachmentComponent } from '@components/ringil3/Features/Attachments/AttachementComponent.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import AcceptedSummary from '@app/pages/carrier/demand-detail/AcceptedSummary.tsx';
import CollapsibleNote from '@components/RingilComponents/RichTextEditor/CollapsibleNote.tsx';
import HighlightedListInfoItem from '@components/obsolete/ListInfo/HighlightedListInfoItem.tsx';

interface ComponentProps {
}

export interface CreateSpotState {
  price: number;
  // @deprecated use utcEnd and timezoneId instead
  selectedData: Dayjs;
  currency: string;
  partialDeliveryUnits?: number;
  timeslots: MakeOfferLocationSlotInDTO[];
  units?: number;
  note?: string;
  transportLegs?: MakeOfferTransportLegInDTO[];
  utcEnd: Dayjs;
  timezoneId: string;
}

const DemandDetailComponentInternal: React.FC<ComponentProps> = () => {
  const { t }: { t: any } = useTranslation('common');
  const { tr } = useTranslationLgs();
  const {
    state,
    rejectQuote,
    makeQuote,
    reconfirm,
    BackendValidationComponent,
  } =
    useContext(DemandDetailContext);
  const [visibleChat, setVisibleChat] = useState(false);
  const { getInheritCompanySettings, caGetCountMessagesV2, listQuotationMaterialsForEntity } = useContext(ApiContext);
  const [displayNewMessageAlert, setDisplayNewMessageAlert] = useState(false);

  const navigation = useNavigation();

  const { internals: attachmentInternals, load: loadAttachments, AttachmentComponent } = useCreateAttachmentComponent(
    {
      enableCopyDocument: false,
      enableDownloadZip: true,
      downloadZipApplicationId: state.applicationId,
    },
    {
      entityType: EntityTypeEnum.EXPEDITION,
      entityId: state.expeditionId,
      disabled: true,
      loadAttachmentsForEntity: listQuotationMaterialsForEntity,
    }
  );

  const quoteState = state.distributionType === DistributionTypeEnum.SPOT ? state.carrierQuoteState : state.customerQuoteState;
  const disabled = state.isExpeditionCanceled || state.isManualExpedition;
  const [locationDisplayPattern, setLocationDisplayPattern] = useState(null);
  const { loggedUser } = useContext(AuthenticatedUserContext);

  const timezoneIdToUse = loggedUser.defaultTimezoneId ?? 'Europe/Prague';

  useEffect(() => {
    getInheritCompanySettings({ identifier: 'LOCATION_DISPLAY_PATTERN' }, state.companyId.toString(), 'CUSTOMER', data => {
      setLocationDisplayPattern(data.payload);
    });
    loadAttachments(EntityTypeEnum.EXPEDITION, state.expeditionId);
  }, []);

  const resolveExpeditionStatus = (state: DemandDetailView) => {
    return (
      <>
        <span
          data-testid="carrier-demand-status"
          data-carrier-quote-state={state.carrierQuoteState ?? ''}
          data-customer-quote-state={state.customerQuoteState ?? ''}
          data-assigned-to-another-carrier={state.isAssignedToAnotherCarrier ? 'true' : 'false'}
          data-distribution-expired={state.isDistributionExpired ? 'true' : 'false'}
          data-expedition-canceled={state.isExpeditionCanceled ? 'true' : 'false'}
          data-manual-expedition={state.isManualExpedition ? 'true' : 'false'}
        >
          <DemandStateSpan
            demandState={state.customerQuoteState}
            isManual={state.isManualExpedition}
            isCanceled={state.isExpeditionCanceled}
          />
        </span>
        {!disabled && (
          <>
            {!state.isAssignedToAnotherCarrier &&
              ((!isNullOrUndefined(state.reconfirmedAtUtc) && !isNullOrUndefined(timezoneIdToUse)) || state.carrierQuoteState === CarrierQuoteStateEnum.ACCEPTED_NEED_RECONFIRM) && (
                <div className="p-mb-3">
                  <ReconfirmQuote
                    onReconfirm={() => reconfirm(state.companyId, state.applicationId)}
                    reconfirmedAtUtc={state.reconfirmedAtUtc}
                    timezoneId={timezoneIdToUse}
                    isReconfirmedByCarrier={state.isReconfirmedByCarrier}
                    disabled={state.isManualExpedition || state.isExpeditionCanceled}
                  />
                </div>
              )}

            <div>
              {t('DemandDetailComponent.validTill', 'Platná do ')}
              <strong
                data-testid="carrier-demand-validTill-header"
              >
                {formatDateTimeFromDayjsInTzWithLocale(dayjsFromApiUtc(state.utcEnd),timezoneIdToUse)}</strong>
              <COTimezone dataTestId="carrier-demand-validTill-header-timezone" timezoneId={timezoneIdToUse} />
            </div>
          </>
        )}
      </>
    );
  };

  useEffect(() => {
    caGetCountMessagesV2(state.companyId, state.expeditionId, EntityTypeEnum.EXPEDITION, data => {
      if (data.countUnread > 0) setDisplayNewMessageAlert(true);
      else setDisplayNewMessageAlert(false);
    });
  }, []);

  const carrierDemandDetailBreadCrumbs = () => {
    return [
      {
        label: t('DemandDetailComponent.demands', 'Demands'),
        url: navigation.createNavigationLink(navigation.urlFunctions.createCarrierDemandsList()),
      },
      {
        label: t('DemandDetailComponent.demandDetail', 'Demand detail'),
        disabled: true,
      },
    ];
  };

  let tooShort;
  if (isNullOrUndefined(state.myQuoteInfo.utcEnd) || isNullOrUndefined(state.myQuoteInfo.timezoneId)) {
    tooShort = dayjs(state.myQuoteInfo.myQuoteValidTill).diff(dayjs(), 'hours') < 1 && dayjs(state.myQuoteInfo.myQuoteValidTill).diff(dayjs(), 'hours') > 0;
  } else {
    tooShort = dayjsFromApiUtc(state.myQuoteInfo.utcEnd).diff(dayjs().utc(), 'hours') < 1
      && dayjsFromApiUtc(state.myQuoteInfo.utcEnd).diff(dayjs().utc(), 'hours') > 0;
  }

  return (
    <>
      <div className="p-mb-5">
        <BreadCrumbComponent items={carrierDemandDetailBreadCrumbs()} />
      </div>
      {tooShort
        && !(quoteState === CustomerQuoteStateEnum.ACCEPTED_BY_CARRIER ||
          quoteState === CustomerQuoteStateEnum.ASSIGNED_MANUALLY_BY_CUSTOMER ||
          quoteState === CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER ||
          quoteState === CarrierQuoteStateEnum.ACCEPTED_NEED_RECONFIRM)
        && (
        <AlertInfo backgroundWrapper="red">
          <p>{tr(`DemandDetailComponent.offerValiditySetTooShort`, `Offer validity set too short`)}</p>
        </AlertInfo>
      )}

      {displayNewMessageAlert && !disabled && (
        <AlertInfo iconName="mail_outline" buttonName={t(`DemandDetailComponent.showMessage`, `Zobrazit zprávu`)}
                   onClick={() => setVisibleChat(true)}>
          <p>{t(`DemandDetailComponent.youHaveANewMessageFromCustomer`, `Máte novou zprávu`)}</p>
        </AlertInfo>
      )}
      {(quoteState === CustomerQuoteStateEnum.ACCEPTED_BY_CARRIER ||
          quoteState === CustomerQuoteStateEnum.ASSIGNED_MANUALLY_BY_CUSTOMER ||
          quoteState === CarrierQuoteStateEnum.ACCEPTED_BY_CUSTOMER ||
          quoteState === CarrierQuoteStateEnum.ACCEPTED_NEED_RECONFIRM) &&
        !disabled && (
          <>
            <AlertInfo backgroundWrapper="transparent">
              <div className="p-mr-3">
                <p>{t('DemandDetailComponent.findDetailInShipments', 'You can find detail in all shipments list or here')}</p>
              </div>
              {state.childExpeditions && state.childExpeditions.length > 0 ? (
                <div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {[...state.childExpeditions]
                      .filter(exp => exp.expeditionStatus !== ExpeditionStatusEnum.CANCELED)
                      .sort((a, b) => a.applicationId.localeCompare(b.applicationId))
                      .map(item => (
                        <RingilButton
                          key={`${item.customerId}-${item.applicationId}`}
                          mode="filled-rectangle"
                          label={item.applicationId}
                          onClick={() =>
                            navigation.navigate(
                              navigation.urlFunctions.createCarrierShipmentDetail(item.customerId, item.applicationId),
                            )
                          }
                        />
                      ))}
                  </div>
                  <div className="mt-2">
                    <AcceptedSummary childExpeditions={
                      [...state.childExpeditions]
                        .filter(exp => exp.expeditionStatus !== ExpeditionStatusEnum.CANCELED)
                    }
                    />
                  </div>
                </div>
              ) : (
                <RingilButton
                  mode="filled-rectangle"
                  label={t('DemandDetailComponent.goToShipmentDetail', 'Go to shipment details')}
                  onClick={() =>
                    navigation.navigate(
                      navigation.urlFunctions.createCarrierShipmentDetail(state.companyId, state.applicationId)
                    )
                  }
                />
              )}
            </AlertInfo>
          </>
        )}
      <BackendValidationComponent />
      <div className="flex flex-col lg:flex-row gap-4 items-start">
        <div className="w-full lg:flex-1 lg:min-w-0">
          <NavTitle wrapper="WITHOUT-COL_PADDING-BOOTOM-4">
            <h2 className="h2-title p-m-0" data-testid="carrier-demand-applicationId" data-application-id={state.applicationId}>
              {t('demandDetail.pageTitle', {
                applicationId: state.applicationId,
              })}{' '}
              <span></span>
            </h2>
            <div>{resolveExpeditionStatus(state)}</div>
          </NavTitle>
          <span
            data-testid="carrier-demand-customerCompanyName"
            data-customer-company-name={state.customer?.name ?? ''}
            style={{ display: 'none' }}
          />
          <ListInfo>
            <ListInfoItem title={t(`DemandDetailComponent.distance`, `Distance`)}>
              <p>
                <small>{state.distance ? state.distance : '-'} km</small>
              </p>
            </ListInfoItem>
            <HighlightedListInfoItem
              title={t(`DemandDetailComponent.noteToCarriers`, `Note to carriers`)}
              highlighted={!isNullOrUndefined(state.carrierNote)}>
              {!isNullOrUndefined(state.carrierNote) ? (
                <CollapsibleNote dataTestId="carrier-demand-noteForCarrier" html={state.carrierNote} />
              ) : (
                <p>
                  <small data-testid="carrier-demand-noteForCarrier">-</small>
                </p>
              )}
            </HighlightedListInfoItem>
            <HighlightedListInfoItem
              title={t('RouteDescriptionComponent.requirements', 'Požadavky')}
              highlighted={state.requirements?.length > 0}>
              <p>
                <small data-testid="carrier-demand-requirements">{state.requirements?.length > 0 ? state.requirements.join(', ') : '-'}</small>
              </p>
            </HighlightedListInfoItem>
            <ListInfoItem title={t(`DemandDetailComponent.expeditionType`, `Expedition type`)}>
              <p>
                <small data-testid="carrier-demand-expeditionType" data-expedition-type={state.expeditionType}>
                  {state.expeditionType ? t(`global.expeditionType${state.expeditionType}`) : tr('global.expeditionTypeNotSpecified', 'Neuvedeno')}
                </small>
              </p>
            </ListInfoItem>
            {state.transportationType && (
              <ListInfoItem title={t(`DemandDetailComponent.transportationType`, `Transportation type`)}>
                <p>
                  <small>{t(`global.transportationType${state.transportationType}`)}</small>
                </p>
              </ListInfoItem>
            )}
            {state.truckType && (
              <ListInfoItem title={t(`DemandDetailComponent.truckType`, `Truck type`)}>
                <p>
                  <small data-testid="carrier-demand-truckType">{state.truckType}</small>
                </p>
              </ListInfoItem>
            )}
            <ListInfoItem title={t(`DemandDetailComponent.maximumWeight`, `Maximum weight`)}>
              <p>
                <small>{formatWeight(state.maxWeight)}</small>
              </p>
            </ListInfoItem>
            <ListInfoItem title={tr(`DemandDetailComponent.totalHandlingUnits`, `Total handling units`)}>
              <p>
                <small>{formatHandlingUnits(state.handlingUnitCount, tr)}</small>
              </p>
            </ListInfoItem>
            <ListInfoItem title={t(`DemandDetailComponent.cargoType`, `Cargo type`)}>
              <p>
                <small data-testid="carrier-demand-cargoType">{state.cargoType}</small>
                {state.adrWeight && (
                  <small> - {formatWeight(state.adrWeight)}</small>
                )}
              </p>
            </ListInfoItem>
            {state.distributionType === DistributionTypeEnum.SPOT && (
              <ListInfoItem title={t(`DemandDetailComponent.expectedPrice`, `Expected price`)}>
                <p>
                  <small>{formatPrice(state.price, state.currency)}</small>
                </p>
              </ListInfoItem>
            )}
            {state.incoterms && (
              <ListInfoItem title={t(`DemandDetailComponent.incoterms`, `Incoterms`)}>
                <p>
                  <small>
                    <MultipleValues
                      values={[state.incoterms.incotermsValue, state.incoterms.incotermsDescription]}></MultipleValues>
                  </small>
                </p>
              </ListInfoItem>
            )}
          </ListInfo>
          <span
            data-testid="carrier-demand-route-first"
            data-location={state.locations?.[0]?.locationName ?? ''}
            style={{ display: 'none' }}
          />
          <span
            data-testid="carrier-demand-route-last"
            data-location={state.locations?.[state.locations.length - 1]?.locationName ?? ''}
            style={{ display: 'none' }}
          />
          <RouteDescriptionComponent enableAction={!state.isExpeditionCanceled && !state.isManualExpedition}
                                     locationDisplayPattern={locationDisplayPattern} />
          <CarrierChatComponent
            companyId={state.companyId}
            entityId={state.expeditionId}
            entityType={EntityTypeEnum.EXPEDITION}
            visible={visibleChat}
            onHide={() => setVisibleChat(false)}
            customerName={state.customer.name}
          />
        </div>

        <div className="w-full min-w-[450px] lg:flex-1 lg:w-auto">
          {state.distributionType === DistributionTypeEnum.FIXED_PRICE && (
            <>
              <CarrierQuote
                quote={state}
                messageOnClick={() => setVisibleChat(true)}
                offerType={OfferTypeEnum.FIXED}
                onReject={() => rejectQuote(state.companyId, state.applicationId, state.quoteId, DistributionTypeEnum.FIXED_PRICE)}
                onAccept={(data, onFinish) => makeQuote(state.companyId, state.applicationId, state.quoteId, data, DistributionTypeEnum.FIXED_PRICE, onFinish)}
              />
            </>

          )}

          {state.distributionType === DistributionTypeEnum.SPOT && (
                <CarrierQuote
                  quote={state}
                  messageOnClick={() => setVisibleChat(true)}
                  offerType={OfferTypeEnum.SPOT}
                  onReject={() => rejectQuote(state.companyId, state.applicationId, state.quoteId, DistributionTypeEnum.SPOT)}
                  onAccept={(data, onFinish) => makeQuote(state.companyId, state.applicationId, state.quoteId, data, DistributionTypeEnum.SPOT, onFinish)}
                />
            )}
        </div>
      </div>
      <AttachmentComponent
        internals={attachmentInternals}
        entityId={state.expeditionId}
        entityType={EntityTypeEnum.EXPEDITION}
        disabledMassAction
        disabledDelete
        allowAddNewAttachment={false}
        allowEditAttachment={false}
      />
    </>
  );
};

type ParsedIds = [string, string];

const DemandDetailComponent = () => {
  const [quoteDetail, setQuoteDetail] = useState<QuoteDetailOutDTO>();

  const { getQuoteDetailById, caGetDemandDetail } = useContext(ApiContext);

  const { id } = useParams();

  const [customerId, applicationId] = parseQuickMongoIdOrUuid(id);

  function parseQuickMongoIdOrUuid(input: string): ParsedIds {
    const firstDashIndex = input.indexOf('-');

    if (firstDashIndex === -1) {
      throw new Error('Invalid format: No dash found in input.');
    }

    let customerId, applicationId;

    if (firstDashIndex === 8) {
      // UUID (36 znaků celkem)
      if (/^[a-f\d]{8}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{4}-[a-f\d]{12}$/.test(input.slice(0, 36))) {
        customerId = input.slice(0, 36); // Prvních 36 znaků je UUID
        applicationId = input.slice(37); // Zbytek po pomlčce
      } else {
        throw new Error('Invalid format: Expected a valid UUID.');
      }
    } else {
      // MongoID (24 znaků)
      if (/^[a-f\d]{24}$/.test(input.slice(0, 24))) {
        customerId = input.slice(0, 24); // Prvních 24 znaků je MongoID
        applicationId = input.slice(25); // Zbytek po pomlčce
      } else {
        throw new Error('Invalid format: Expected a valid MongoID.');
      }
    }

    return [customerId, applicationId];
  };


  useEffect(() => {
    if (applicationId) {
      caGetDemandDetail(customerId, applicationId, data => {
          setQuoteDetail(data);
        });
    } else {
      getQuoteDetailById(id, data => {
        setQuoteDetail(data);
      });
    }

  }, []);

  return (
    <>
      {quoteDetail && (
        <DemandDataProvider initialData={quoteDetail}>
          <DemandDetailComponentInternal />
        </DemandDataProvider>
      )}
    </>
  );
};

export default DemandDetailComponent;
